import React, { useContext, useState } from 'react';
import * as PropTypes from 'prop-types';
import styled from 'styled-components';
import { Popover, Typography } from '@mui/material';
import { grey } from '@mui/material/colors';
import { ArrowDropDown } from '@mui/icons-material';
import classnames from 'classnames';

import { t } from '../../../../../utils/i18next';
import PaymentButtonWrapper from '../../../general/PaymentButtonWrapper';
import SystemButton from '../../../general/SystemButton';
import { spacingInRems } from '../../../../theme/themeUtils';
import { storeContext } from '../../../../context';
import CurrencyFormat from '../../../general/CurrencyFormat';
import PaymentItem from './PaymentItem/PaymentItem.js';
import { numberToCurrency } from '../../../../../utils/String';

const StyledArrowDropDown = styled(ArrowDropDown)`
  &&.arrowTop {
    transform: rotate(180deg);
  }
`;
const StyledPopover = styled(Popover)`
  && {
    .paper {
      display: flex;
      flex-direction: column;
      button {
        color: ${({ theme }) => theme.palette.text.primary};
        background-color: ${grey[50]};
        border-radius: 0;
        padding: ${spacingInRems()};
        box-shadow: none;
        min-width: 26rem;
        &:hover {
          background-color: ${grey[300]};
        }
        .textContainer {
          display: flex;
          justify-content: space-between;
          width: 100%;
          .paymentText {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
          }
          .duration {
            color: ${({ theme }) => theme.palette.secondary.main};
          }
        }
        .text {
          width: 100%;
          & > p {
            display: flex;
            justify-content: space-between;
            gap: 1rem;
          }
          a {
            color: ${({ theme }) => theme.palette.text.primary};
            &:hover {
              text-decoration: none;
            }
            .textContainer {
              & > span {
                margin-right: 0.5rem;
              }
            }
          }
          .serviceFee {
            font-size: 1rem;
            text-transform: none;
          }
        }
      }
    }
  }
`;

const assistTranslationBase = 'payables.total_side_bar.assist';

const PopoverButton = props => {
  const { paymentOptions, buttonText, buttonColor } = props;
  const {
    translations: { currentLanguage },
    userState
  } = useContext(storeContext);

  const { ANCHOR_TO_BOTTOM } = PaymentItem.popoverTypes;
  const [anchorEl, setAnchorEl] = useState(null);
  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onDropdownButtonClick = option => {
    handleClose();
  };

  const open = Boolean(anchorEl);
  const id = open ? 'popover' : undefined;
  userState.set('paymentItemPopoverIsOpen', open);

  const getDropdownButtons = () =>
    paymentOptions.map((option, index) => (
      <PaymentButtonWrapper
        variant="contained"
        key={index}
        onClick={() => onDropdownButtonClick(option)}
        aria-label={`${t(option.text)} ${numberToCurrency(option.amount)}`}
        aria-expanded={open}
        {...option}
      >
        <Typography className="textContainer">
          <span className="paymentText">
            <span>{t(option.text)}</span>
            {option.subText && (
              <Typography component="span" variant="caption">
                {t(option.subText)}
              </Typography>
            )}
          </span>
          <span className={classnames({ duration: option.duration })}>
            <CurrencyFormat value={option.amount} />
            {option.duration && (
              <span>
                {t(`${assistTranslationBase}.duration`, {
                  months: option.duration
                })}
              </span>
            )}
          </span>
        </Typography>
        {!!option.servicefee && (
          <span className={'serviceFee'} data-testid={'service-fee'}>
            {t(`${assistTranslationBase}.service_fee.text`, {
              val: numberToCurrency(option.servicefee)
            })}
          </span>
        )}
      </PaymentButtonWrapper>
    ));

  const anchorToBottom =
    userState['paymentItemPopoverType'] === ANCHOR_TO_BOTTOM;

  return (
    <>
      <SystemButton
        data-testid="popover-button-see-payment-options"
        variant="contained"
        color={buttonColor}
        size="large"
        aria-label={t(buttonText)}
        onClick={handleClick}
      >
        {t(buttonText)}
        <StyledArrowDropDown
          className={anchorToBottom ? 'arrowBottom' : 'arrowTop'}
        />
      </SystemButton>

      <StyledPopover
        id={id}
        data-testid={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        PaperProps={{ className: 'paper', 'data-testid': 'popoverPaper' }}
        anchorOrigin={{
          vertical: anchorToBottom ? 'bottom' : 'top',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: anchorToBottom ? 'top' : 'bottom',
          horizontal: 'center'
        }}
      >
        {getDropdownButtons()}
      </StyledPopover>
    </>
  );
};

PopoverButton.propTypes = {
  paymentOptions: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      subText: PropTypes.string,
      amount: PropTypes.number,
      duration: PropTypes.number,
      servicefee: PropTypes.number,
      disabled: PropTypes.bool
    })
  ).isRequired,
  buttonText: PropTypes.string.isRequired,
  buttonColor: PropTypes.string.isRequired
};

export default PopoverButton;
